// services/worktime.js — нощен труд (чл. 140 КТ + чл. 8 НСОРЗ), сумирано
// изчисляване на работното време (чл. 142 КТ), труд на официален празник
// (чл. 264 КТ) и болничният за сметка на работодателя (чл. 40, ал. 5 КСО).
// Чисти функции; работни дни и норми — само през holidays.js; МРЗ — от
// таблицата в liability.js. Променливите стойности са в конфигурационни
// таблици по дата с източник.
//
// СВЕРЕНО ПРИ СТРОЕЖА (изричното „да се провери" от каталога):
//  - нощната добавка е процент от МРЗ (0,15% на час) с долен праг, а не
//    фиксирани стотинки — старите „0,25 лв." са отдавна отменени;
//  - работодателят плаща първите ДВА работни дни от болничния (не три) —
//    изм. на чл. 40, ал. 5 КСО в сила от 01.01.2024.

const holidays = require('./holidays');
const { round2 } = require('./totals');
const { minWageAt } = require('./liability');

// ---- Г2. Нощен труд ----

// Нощен е трудът от 22:00 до 06:00 (чл. 140, ал. 2 КТ).
const NIGHT_FROM_MIN = 22 * 60;
const NIGHT_TO_MIN = 30 * 60; // 06:00 на следващия ден, в минути от 00:00

// Добавката по чл. 8 НСОРЗ: не по-малко от 0,15% от МРЗ за всеки нощен
// час, но не по-малко от долния праг (1 лв. = 0,51 €). Таблица по дата,
// за да не се рови в кода при следваща промяна.
const NIGHT_RATE_TABLE = [
  {
    from: '2025-01-01', pctOfMinWage: 0.15, floorEur: 0.51,
    source: 'чл. 8 НСОРЗ — 0,15% от МРЗ на нощен час, под 1 лв. (0,51 €); сверено 08.2026',
  },
];

function nightUplift(o) {
  const date = String((o && o.date) || '').slice(0, 10);
  const row = NIGHT_RATE_TABLE.filter((r) => r.from <= date).sort((a, b) => (a.from < b.from ? 1 : -1))[0];
  if (!row) throw new Error('Няма въведена ставка за нощен труд към ' + date + ' — добави ред в NIGHT_RATE_TABLE.');
  const mw = minWageAt(date);
  const pct = mw * row.pctOfMinWage / 100;
  const floorApplied = pct < row.floorEur;
  const perHour = floorApplied ? row.floorEur : pct;
  return {
    perHour,
    floorApplied,
    minWage: mw,
    source: row.source + ' (МРЗ ' + mw + ' €)',
    pay: (hours) => round2(perHour * (Number(hours) || 0)),
  };
}

// Колко от часовете на една смяна попадат в нощния диапазон 22:00–06:00.
// Смяната се разглежда като интервал в минути; ако краят е ≤ началото,
// минава през полунощ. Нощният прозорец се повтаря на всеки 24 ч. —
// проверяват се трите съседни повторения.
function nightHoursInShift(o) {
  const toMin = (s) => { const [h, m] = String(s).split(':').map(Number); return h * 60 + (m || 0); };
  const start = toMin(o.from);
  let end = toMin(o.to);
  if (end <= start) end += 1440;
  let night = 0;
  for (let k = -1; k <= 1; k++) {
    const a = Math.max(start, NIGHT_FROM_MIN + k * 1440);
    const b = Math.min(end, NIGHT_TO_MIN + k * 1440);
    if (b > a) night += b - a;
  }
  return night / 60;
}

// ---- Г3. СИРВ (чл. 142 КТ) ----

// При сумирано изчисляване нощните часове се превръщат в дневни с
// коефициент 8/7 (чл. 9а НРВПО — съотношението на нормалната
// продължителност: 8 ч. дневно / 7 ч. нощно).
const sirvNightFactor = 8 / 7;

// Периодът на сумиране е до 4 месеца; с КТД — до 12 за определени
// дейности (чл. 142, ал. 2 и 4).
const SIRV_MAX_MONTHS = 4;

// Баланс за периода: нормата идва от РЕАЛНИТЕ работни дни на месеците
// (през holidays.js), не от подадено число. Каквото е отгоре, е
// извънреден труд (заплаща се с 50% увеличение — чл. 262, ал. 1, т. 4).
function sirvBalance(o) {
  const year = Number(o.year);
  const hoursPerDay = Number(o.hoursPerDay) || 8;
  let workDays = 0;
  const mFrom = Number(o.monthFrom);
  const mTo = Number(o.monthTo);
  for (let m = mFrom; m <= mTo; m++) {
    const last = new Date(Date.UTC(year, m, 0)).getUTCDate();
    const mm = (m < 10 ? '0' : '') + m;
    workDays += holidays.workingDaysBetween(year + '-' + mm + '-01', year + '-' + mm + '-' + last, o.overrides);
  }
  const normHours = workDays * hoursPerDay;
  const convertedNight = round2((Number(o.nightHours) || 0) * sirvNightFactor);
  const totalWorked = round2((Number(o.workedHours) || 0) + convertedNight);
  const notes = [];
  if (mTo - mFrom + 1 > SIRV_MAX_MONTHS) {
    notes.push('Период над 4 месеца е допустим само с КТД за определени дейности (чл. 142, ал. 4).');
  }
  return {
    workDays, normHours, convertedNight, totalWorked,
    overtime: Math.max(0, round2(totalWorked - normHours)),
    notes,
  };
}

// ---- Г5. Труд на официален празник (чл. 264 КТ) ----

// За работа на официален празник — по график или не — се заплаща не
// по-малко от УДВОЕНИЯ размер на уговореното възнаграждение. Ако трудът
// е и извънреден, отгоре се дължи увеличението по чл. 262, ал. 1, т. 3
// (100%) — двете основания се кумулират (практика на ВКС).
function holidayWorkPay(o) {
  const base = round2((Number(o.hours) || 0) * (Number(o.rate) || 0));
  const notes = ['Удвоено възнаграждение по чл. 264 — дължи се независимо дали работата е по график.'];
  let total = round2(base * 2);
  if (o.alsoOvertime) {
    total = round2(base * 3);
    notes.push('Извънреден труд на празник → отгоре и 100% увеличение по чл. 262, ал. 1, т. 3 (кумулират се, ВКС).');
  }
  return { base, doubled: round2(base * 2), total, notes };
}

// ---- Г6. Болничен — дните за сметка на работодателя (чл. 40, ал. 5 КСО) ----

// От 01.01.2024 работодателят плаща първите ДВА работни дни от
// неработоспособността, 70% от среднодневното УГОВОРЕНО възнаграждение.
// Таблица по дата — при следваща промяна се добавя ред, не се пипа код.
const EMPLOYER_SICK_TABLE = [
  {
    from: '2024-01-01', workDays: 2, percent: 70,
    source: 'чл. 40, ал. 5 КСО, в сила от 01.01.2024 (дотогава: първите 3 работни дни); сверено 08.2026',
  },
];

function employerSickPay(o) {
  const from = String(o.from).slice(0, 10);
  const to = String(o.to).slice(0, 10);
  const row = EMPLOYER_SICK_TABLE.filter((r) => r.from <= from).sort((a, b) => (a.from < b.from ? 1 : -1))[0];
  if (!row) throw new Error('Няма въведен режим за болничен към ' + from + ' — добави ред в EMPLOYER_SICK_TABLE.');
  // първите N РАБОТНИ дни в периода на болничния — броени през holidays.js
  const days = [];
  for (let cur = from; cur <= to && days.length < row.workDays; cur = holidays.addDays(cur, 1)) {
    if (holidays.isWorkingDay(cur, o.overrides)) days.push(cur);
  }
  const daily = Number(o.dailyAgreed) || 0;
  return {
    days,
    percent: row.percent,
    amount: round2(days.length * daily * row.percent / 100),
    source: row.source,
    notes: [
      'От 3-ия работен ден плаща НОИ — 80% от среднодневния ОСИГУРИТЕЛЕН доход (различна база, чл. 41 КСО).',
      'Базата тук е среднодневното УГОВОРЕНО възнаграждение за месеца на болничния.',
    ],
  };
}

module.exports = {
  nightUplift, nightHoursInShift, sirvNightFactor, sirvBalance,
  holidayWorkPay, employerSickPay,
  NIGHT_RATE_TABLE, EMPLOYER_SICK_TABLE, SIRV_MAX_MONTHS,
};
